import { CustomSettings } from "subModule/src/services/invoice/types";

export const THERMAL_WIDTH_PRESETS = [112, 80, 58] as const;
export const THERMAL_MAX_WIDTH_MM = 210;
export const THERMAL_MIN_WIDTH_MM = 40;

export function getThermalPaperWidthMm(settings: CustomSettings): number {
  const preset = settings.thermalPaperWidthPreset ?? "80";
  if (preset === "other") {
    const custom = Number(settings.thermalCustomWidthMm) || 80;
    return Math.min(
      Math.max(custom, THERMAL_MIN_WIDTH_MM),
      THERMAL_MAX_WIDTH_MM,
    );
  }
  return Number(preset);
}

export function getThermalBaseFontSize(widthMm: number): number {
  if (widthMm <= 58) return 8;
  if (widthMm <= 80) return 9;
  return 10;
}

export function isThermalPrint(settings: CustomSettings): boolean {
  return settings.printFormat === "thermal";
}

/** Section ids used for invoice thermal receipts (CMS + POS must stay in sync) */
export const THERMAL_INVOICE_SECTION_ORDER_DEFAULT = [
  "logo",
  "header",
  "table",
  "summary",
  "notes",
  "qr",
  "footer",
] as const;

const THERMAL_INVALID_SECTION_IDS = new Set([
  "vehicleInfo",
  "terms",
  "inspectionItems",
  "customerSignature",
  "vehicleImage",
]);

/**
 * Normalizes section order: keeps only thermal section ids, drops vehicle/inspection,
 * and sorts by the user's `sectionOrder` when present; missing sections use default
 * relative order (so `qr` stays before `footer` unless the user moved it).
 */
export function normalizeThermalSectionOrder(
  order?: string[] | null,
): string[] {
  const defaultOrder = [...THERMAL_INVOICE_SECTION_ORDER_DEFAULT];
  const allowed = new Set<string>(defaultOrder);
  const filtered = (order || []).filter(
    (id) => allowed.has(id) && !THERMAL_INVALID_SECTION_IDS.has(id),
  );
  const userIndex = new Map<string, number>();
  filtered.forEach((id, i) => {
    if (!userIndex.has(id)) userIndex.set(id, i);
  });
  return [...defaultOrder].sort((a, b) => {
    const ia = userIndex.has(a)
      ? userIndex.get(a)!
      : 1000 + defaultOrder.indexOf(a);
    const ib = userIndex.has(b)
      ? userIndex.get(b)!
      : 1000 + defaultOrder.indexOf(b);
    return ia - ib;
  });
}

export const THERMAL_INVOICE_DEFAULTS: Partial<CustomSettings> = {
  printFormat: "thermal",
  thermalPaperWidthPreset: "80",
  thermalCustomWidthMm: 80,
  /** Thermal receipts omit vehicle details and terms */
  showVehicleInfo: false,
  showTerms: false,
  sectionOrder: [...THERMAL_INVOICE_SECTION_ORDER_DEFAULT],
  showInvoiceDirection: "column",
  useDefaultLogoSize: false,
  logoWidth: 56,
  logoHeight: 36,
  invoiceNumberFontSize: 11,
  invoiceNumberFontWeight: 700,
  /** QR toggle on; rendered in the draggable `qr` section (typically last before footer) */
  showQR: true,
};
